import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Wallet, DollarSign, AlertCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  // Show up to 2 decimals when the value has a fractional part so the user can
  // spot any non-integer rounding artefacts in real time.
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

// For the imbalance difference: reveal fractional cents when non-zero so
// even a 0,1 selisih is visible to the user (strict balance enforcement).
const formatDifference = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

const BalanceSheetHeader = ({ periodData, isBalanced, difference }) => (
  <CardHeader className="bg-gradient-to-r from-green-50 via-red-50 to-blue-50">
    <div className="flex items-center justify-between">
      <CardTitle className="text-2xl font-bold text-slate-800">
        Balance Sheet - Periode: {periodData ? periodData.name : 'Semua Periode'}
      </CardTitle>
      {isBalanced ? (
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg border border-green-300">
          <TrendingUp size={16} />
          <span className="font-semibold text-sm">Seimbang ✓</span>
        </div>
      ) : (
        <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg border border-yellow-300">
          <AlertCircle size={16} />
          <span className="font-semibold text-sm">
            Tidak Seimbang ({formatDifference(difference)})
          </span>
        </div>
      )}
    </div>
  </CardHeader>
);

const AssetsSection = ({ 
  balanceSheet, 
  periodTransfers, 
  inventoryDetails,
  onInventoryClick,
  onTransferClick 
}) => (
  <div className="space-y-3">
    <h2 className="text-lg font-bold text-green-800 border-b-2 border-green-600 pb-2 flex items-center gap-2">
      <Wallet size={20} />
      ASET (ASSETS)
    </h2>
    
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-600 mt-2">Kas & Bank:</h3>
      
      <div className="flex justify-between items-center text-sm pl-3">
        <span className="text-slate-700">• Rekening Pusat</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.centralBalance)}
        </span>
      </div>

      <div className="flex justify-between items-center text-sm pl-3">
        <span className="text-slate-700">• Rekening Cabang</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.branchBalance)}
        </span>
      </div>

      <div className="flex justify-between items-center text-sm pl-3">
        <span className="text-slate-700">• Dana Gantung</span>
        <span className="font-semibold text-orange-600">
          {formatCurrency(balanceSheet.pendingMoney)}
        </span>
      </div>

      <div className="flex justify-between items-center text-sm border-t pt-1 mt-1">
        <span className="text-slate-600">Subtotal Kas & Bank</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.cash)}
        </span>
      </div>

      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-700">Piutang</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.customerBalance)}
        </span>
      </div>

      <div 
        className={`flex justify-between items-center text-sm ${inventoryDetails?.length > 0 ? 'cursor-pointer hover:bg-purple-50' : ''}`}
        onClick={() => inventoryDetails?.length > 0 && onInventoryClick?.()}
        title={inventoryDetails?.length > 0 ? 'Klik untuk melihat detail persediaan per cabang' : ''}
      >
        <span className="text-slate-700">Persediaan</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.inventory)}
        </span>
      </div>

      <div 
        className={`flex justify-between items-center text-sm ${periodTransfers?.details?.length > 0 ? 'cursor-pointer hover:bg-blue-50' : ''}`}
        onClick={() => periodTransfers?.details?.length > 0 && onTransferClick?.()}
        title={periodTransfers?.details?.length > 0 ? 'Klik untuk melihat detail' : ''}
      >
        <span className="text-slate-700">Pindah Periode</span>
        <span className={`font-semibold ${periodTransfers?.total < 0 ? 'text-red-600' : 'text-slate-900'}`}>
          {formatCurrency(periodTransfers?.total || 0)}
        </span>
      </div>

      <div className="flex justify-between items-center pt-2 border-t-2 border-green-600 bg-green-50 px-2 py-2 rounded">
        <span className="font-bold text-green-900">TOTAL ASET</span>
        <span className="font-bold text-green-900 text-lg">
          {formatCurrency(balanceSheet.totalAssets + (periodTransfers?.total || 0))}
        </span>
      </div>
    </div>
  </div>
);

const LiabilitiesSection = ({ balanceSheet }) => (
  <div className="space-y-3">
    <h2 className="text-lg font-bold text-red-800 border-b-2 border-red-600 pb-2 flex items-center gap-2">
      <AlertCircle size={20} />
      KEWAJIBAN
    </h2>
    
    <div className="space-y-2">
      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-700">Hutang Usaha</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.hutangUsaha || 0)}
        </span>
      </div>

      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-700">Laba Rugi Mitra</span>
        <span className="font-semibold text-slate-900">
          {formatCurrency(balanceSheet.labaRugiMitra || 0)}
        </span>
      </div>

      <div className="flex justify-between items-center pt-2 border-t-2 border-red-600 bg-red-50 px-2 py-2 rounded">
        <span className="font-bold text-red-900">TOTAL KEWAJIBAN</span>
        <span className="font-bold text-red-900 text-lg">
          {formatCurrency(balanceSheet.totalLiabilities)}
        </span>
      </div>
    </div>
  </div>
);

const EquitySection = ({ balanceSheet, previousPeriodRetainedEarnings }) => {
  const prevEarnings = previousPeriodRetainedEarnings && !previousPeriodRetainedEarnings.loading 
    ? previousPeriodRetainedEarnings 
    : null;

  const totalEquity = balanceSheet.totalEquity + (
    prevEarnings ? prevEarnings.labaDitahanMitra + prevEarnings.labaDitahanInvestor : 0
  );

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold text-blue-800 border-b-2 border-blue-600 pb-2 flex items-center gap-2">
        <DollarSign size={20} />
        MODAL (EQUITY)
      </h2>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-700">Modal Investor</span>
          <span className="font-semibold text-slate-900">
            {formatCurrency(balanceSheet.investorCapital)}
          </span>
        </div>

        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-700">Total Investasi Mitra</span>
          <span className="font-semibold text-slate-900" data-testid="total-investasi-mitra">
            {formatCurrency(balanceSheet.totalInvestasiMitra || 0)}
          </span>
        </div>

        {prevEarnings && (
          <>
            <div className="flex justify-between items-center text-sm bg-amber-50 px-2 py-1 rounded">
              <span className="text-slate-700">Laba Ditahan (Mitra) - {prevEarnings.periodName}</span>
              <span className={`font-semibold ${prevEarnings.labaDitahanMitra >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(prevEarnings.labaDitahanMitra)}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm bg-amber-50 px-2 py-1 rounded">
              <span className="text-slate-700">Laba Ditahan (Investor) - {prevEarnings.periodName}</span>
              <span className={`font-semibold ${prevEarnings.labaDitahanInvestor >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(prevEarnings.labaDitahanInvestor)}
              </span>
            </div>
          </>
        )}

        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-700">Laba Ditahan (Mitra)</span>
          <span className={`font-semibold ${balanceSheet.labaDitahanCabang >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(balanceSheet.labaDitahanCabang)}
          </span>
        </div>

        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-700">Laba Ditahan (Investor)</span>
          <span className={`font-semibold ${balanceSheet.labaDitahanInvestor >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(balanceSheet.labaDitahanInvestor)}
          </span>
        </div>

        <div className="flex justify-between items-center pt-2 border-t-2 border-blue-600 bg-blue-50 px-2 py-2 rounded">
          <span className="font-bold text-blue-900">TOTAL MODAL</span>
          <span className="font-bold text-blue-900 text-lg">
            {formatCurrency(totalEquity)}
          </span>
        </div>
      </div>
    </div>
  );
};

const BalanceSheetCard = ({
  periodData,
  balanceSheet,
  periodTransfers,
  inventoryDetails,
  previousPeriodRetainedEarnings,
  isActuallyBalanced,
  actualBalanceDifference,
  onInventoryClick,
  onTransferClick
}) => {
  return (
    <Card className="border-2 border-teal-200">
      <BalanceSheetHeader 
        periodData={periodData} 
        isBalanced={isActuallyBalanced} 
        difference={actualBalanceDifference} 
      />
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ASSETS (ASET) - Left Column */}
          <AssetsSection 
            balanceSheet={balanceSheet}
            periodTransfers={periodTransfers}
            inventoryDetails={inventoryDetails}
            onInventoryClick={onInventoryClick}
            onTransferClick={onTransferClick}
          />

          {/* LIABILITIES & EQUITY - Right Column (Stacked) */}
          <div className="space-y-6">
            <LiabilitiesSection balanceSheet={balanceSheet} />
            <EquitySection 
              balanceSheet={balanceSheet} 
              previousPeriodRetainedEarnings={previousPeriodRetainedEarnings}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BalanceSheetCard;
